import React, { createContext, useContext, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { login as loginAction, logout as logoutAction } from '../store/slices/authSlice';
import api from '../utils/axios';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  tenantId: string;
  profileImage?: string;
  phone?: string;
  bio?: string;
  created_at?: string;
  updated_at?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (token: string, user: User) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();

  const loadUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      const userData = (response.data as any).user;
      setUser(userData);
      setError(null);
      
      // Also update Redux store
      if (userData) {
        dispatch(loginAction({ token, user: userData }));
      }
    } catch (error) {
      console.error('Error loading user:', error);
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const login = async (token: string, user: User) => {
    localStorage.setItem('token', token);
    setUser(user);
    setError(null);
    
    // Also update Redux store
    dispatch(loginAction({ token, user }));
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    
    // Also update Redux store
    dispatch(logoutAction());
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await api.get('/auth/me');
      const userData = (response.data as any).user;
      setUser(userData);
      setError(null);
      
      // Also update Redux store
      if (userData) {
        dispatch(loginAction({ token, user: userData }));
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 